import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchHarvestStatus, fetchRetryFailedStatus, resumeHarvest, retryFailedRecords, stopHarvest, triggerBulkHarvest, } from "../api/adminService";
import { HarvestAlreadyRunningError } from "../types/Admin";
export var useSkcHarvest = function () {
    var _a;
    var kyInstance = useOkapiKy();
    var _b = useState("2024-01-01"), fromDate = _b[0], setFromDate = _b[1];
    var _c = useState(false), force = _c[0], setForce = _c[1];
    var _d = useState("idle"), flowState = _d[0], setFlowState = _d[1];
    var _e = useState(null), errorContent = _e[0], setErrorContent = _e[1];
    var _f = useState(null), currentRunId = _f[0], setCurrentRunId = _f[1];
    var _g = useState(false), retryPolling = _g[0], setRetryPolling = _g[1];
    var _h = useState(null), retryResult = _h[0], setRetryResult = _h[1];
    var flowStateRef = useRef(flowState);
    flowStateRef.current = flowState;
    var currentRunIdRef = useRef(currentRunId);
    currentRunIdRef.current = currentRunId;
    var _j = useQuery(["harvest-skc-status"], function () { return fetchHarvestStatus(kyInstance); }, {
        refetchInterval: function (data) { return ((data === null || data === void 0 ? void 0 : data.status) === "RUNNING" ? 3000 : false); },
        onSuccess: function (data) {
            var _a;
            if (flowStateRef.current !== "syncing" ||
                (data === null || data === void 0 ? void 0 : data.id) !== currentRunIdRef.current)
                return;
            if (data.status === "COMPLETED") {
                setFlowState("success");
            }
            if (data.status === "FAILED") {
                setErrorContent((_a = data.errorMessage) !== null && _a !== void 0 ? _a : null);
                setFlowState("error");
            }
            if (data.status === "INTERRUPTED") {
                setFlowState("idle");
            }
        },
        onError: function () {
            if (flowStateRef.current === "syncing") {
                setFlowState("error");
            }
        },
    }), harvestStatus = _j.data, isStatusLoading = _j.isLoading, refetchStatus = _j.refetch;
    var _k = useMutation(function () { return triggerBulkHarvest(kyInstance, fromDate, force); }, {
        onSuccess: function (response) {
            setCurrentRunId(response.runId);
            setFlowState("syncing");
            refetchStatus();
        },
        onError: function (error) {
            if (error instanceof HarvestAlreadyRunningError) {
                setFlowState("alreadyRunning");
            }
            else {
                setErrorContent(error instanceof Error ? error.message : String(error));
                setFlowState("error");
            }
        },
    }), startSync = _k.mutate, isStarting = _k.isLoading;
    var _l = useMutation(function () { var _a; return resumeHarvest(kyInstance, (_a = harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.id) !== null && _a !== void 0 ? _a : ""); }, {
        onSuccess: function (response) {
            setCurrentRunId(response.runId);
            setFlowState("syncing");
            refetchStatus();
        },
        onError: function (error) {
            if (error instanceof HarvestAlreadyRunningError) {
                setFlowState("alreadyRunning");
            }
            else {
                setErrorContent(error instanceof Error ? error.message : String(error));
                setFlowState("error");
            }
        },
    }), resumeSync = _l.mutate, isResuming = _l.isLoading;
    var _m = useMutation(function () { return stopHarvest(kyInstance); }, {
        onSuccess: function () {
            refetchStatus();
        },
        onError: function (error) {
            setErrorContent(error instanceof Error ? error.message : String(error));
            setFlowState("error");
        },
    }), stopSync = _m.mutate, isStopping = _m.isLoading;
    var retryStatus = useQuery(["retry-failed-status"], function () { return fetchRetryFailedStatus(kyInstance); }, {
        enabled: retryPolling,
        refetchInterval: 2000,
        onSuccess: function (data) {
            var _a, _b, _c;
            if (!data.running && retryPolling) {
                setRetryPolling(false);
                setRetryResult({
                    totalProcessed: (_a = data.total) !== null && _a !== void 0 ? _a : 0,
                    succeeded: (_b = data.succeeded) !== null && _b !== void 0 ? _b : 0,
                    failed: (_c = data.failed) !== null && _c !== void 0 ? _c : 0,
                });
                refetchStatus();
            }
        },
    }).data;
    var isRetrying = retryPolling;
    var retryFailed = useMutation(function () { return retryFailedRecords(kyInstance); }, {
        onSuccess: function () {
            setRetryResult(null);
            setRetryPolling(true);
        },
        onError: function (error) {
            if (error instanceof HarvestAlreadyRunningError) {
                setFlowState("alreadyRunning");
            }
            else {
                setErrorContent(error instanceof Error ? error.message : String(error));
                setFlowState("error");
            }
        },
    }).mutate;
    var isRunning = (harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.status) === "RUNNING";
    var canResume = ((harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.status) === "INTERRUPTED" ||
        (harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.status) === "FAILED") &&
        (harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.lastResumptionToken) != null;
    var unresolvedFailedCount = (_a = harvestStatus === null || harvestStatus === void 0 ? void 0 : harvestStatus.unresolvedFailedRecordCount) !== null && _a !== void 0 ? _a : 0;
    var canRetryFailed = unresolvedFailedCount > 0 && !isRunning;
    var closeModal = useCallback(function () {
        setFlowState("idle");
        setErrorContent(null);
        setRetryPolling(false);
        setRetryResult(null);
    }, []);
    return {
        fromDate: fromDate,
        setFromDate: setFromDate,
        force: force,
        setForce: setForce,
        flowState: flowState,
        errorContent: errorContent,
        closeModal: closeModal,
        harvestStatus: harvestStatus,
        isStatusLoading: isStatusLoading,
        startSync: startSync,
        isStarting: isStarting,
        resumeSync: resumeSync,
        isResuming: isResuming,
        stopSync: stopSync,
        isStopping: isStopping,
        isRunning: isRunning,
        canResume: canResume,
        retryFailed: retryFailed,
        isRetrying: isRetrying,
        retryResult: retryResult,
        retryStatus: retryStatus,
        unresolvedFailedCount: unresolvedFailedCount,
        canRetryFailed: canRetryFailed,
    };
};
